import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  Terminal,
  ChevronRight,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Command Mode - Vim-like command mode for advanced users
 * 
 * Enter command mode with `:` key
 * Commands: :new, :save, :export ts, :sim start
 * Command history with ↑/↓
 * Tab completion for commands
 */

const COMMANDS = {
  // Machine operations
  'new': { description: 'Create new machine', action: 'create' },
  'save': { description: 'Save current machine', action: 'save' },
  'delete': { description: 'Delete current machine', action: 'delete' },
  
  // Export commands
  'export': { description: 'Export machine (ts|json|graph)', action: 'export' },
  'export ts': { description: 'Export to TypeScript', action: 'export-ts' },
  'export json': { description: 'Export to JSON Schema', action: 'export-json' },
  'export graph': { description: 'Export to Graph (DOT)', action: 'export-graph' },
  
  // Simulation
  'sim': { description: 'Simulation commands', action: 'sim' },
  'sim start': { description: 'Start simulation', action: 'sim-start' },
  'sim stop': { description: 'Stop simulation', action: 'sim-stop' },
  'sim reset': { description: 'Reset simulation', action: 'sim-reset' },
  
  // View commands
  'view': { description: 'Switch view (graph|dsl|caps)', action: 'view' },
  'view graph': { description: 'Switch to graph view', action: 'view-graph' },
  'view dsl': { description: 'Switch to DSL view', action: 'view-dsl' },
  'view caps': { description: 'Switch to capabilities', action: 'view-caps' },
  
  // Panel commands
  'focus': { description: 'Toggle focus mode', action: 'focus' },
  'workspace': { description: 'Toggle workspace panel', action: 'toggle-workspace' },
  'inspector': { description: 'Toggle inspector panel', action: 'toggle-inspector' },
  
  // State operations
  'add state': { description: 'Add new state', action: 'add-state' },
  'add transition': { description: 'Add new transition', action: 'add-transition' },
  'add effect': { description: 'Add new effect', action: 'add-effect' },
  
  // Testing
  'test': { description: 'Run all tests', action: 'test-all' },
  'test create': { description: 'Create new test', action: 'test-create' },
  
  // Version control
  'checkpoint': { description: 'Create checkpoint', action: 'checkpoint' },
  'history': { description: 'Show version history', action: 'history' },
  
  // Sharing
  'share': { description: 'Share machine', action: 'share' },
  
  // Navigation
  'goto': { description: 'Go to state (name)', action: 'goto' },
  
  // Help
  'help': { description: 'Show help', action: 'help' },
  'shortcuts': { description: 'Show keyboard shortcuts', action: 'shortcuts' },
  
  // Quit
  'q': { description: 'Close current dialog', action: 'close' },
  'quit': { description: 'Close current dialog', action: 'close' }
};

const CommandMode = ({ open, onClose, onCommand }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setInput('');
      setLastResult(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    // Update suggestions based on input
    if (!input) {
      setSuggestions([]);
      return;
    }

    const matching = Object.entries(COMMANDS)
      .filter(([cmd]) => cmd.startsWith(input.toLowerCase()))
      .slice(0, 5);
    
    setSuggestions(matching);
    setSelectedSuggestion(0);
  }, [input]);

  const executeCommand = (cmdString) => {
    const cmd = cmdString.toLowerCase().trim();
    
    // Add to history
    setHistory(prev => [...prev.filter(h => h !== cmd), cmd].slice(-20));
    setHistoryIndex(-1);
    
    // Find command
    const commandEntry = COMMANDS[cmd];
    
    if (commandEntry) {
      const success = onCommand(commandEntry.action, cmd);
      setLastResult({
        command: cmd,
        success: success !== false,
        message: success !== false ? 'Command executed' : 'Command failed'
      });
      
      // Close after successful command (except help)
      if (success !== false && !['help', 'shortcuts'].includes(commandEntry.action)) {
        setTimeout(() => onClose(), 300);
      }
    } else if (cmd.startsWith('goto ')) {
      const stateName = cmd.substring(5).trim();
      const success = onCommand('goto', stateName);
      setLastResult({
        command: cmd,
        success: success !== false,
        message: success !== false ? `Jumped to ${stateName}` : `State "${stateName}" not found`
      });
    } else {
      setLastResult({
        command: cmd,
        success: false,
        message: `Unknown command: ${cmd}`
      });
      toast.error(`Unknown command: ${cmd}`);
    }
    
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && selectedSuggestion >= 0) {
        executeCommand(suggestions[selectedSuggestion][0]);
      } else if (input) {
        executeCommand(input);
      }
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setInput(suggestions[selectedSuggestion][0]);
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setSelectedSuggestion(prev => Math.max(0, prev - 1));
      } else if (history.length > 0) {
        const newIndex = Math.min(historyIndex + 1, history.length - 1);
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex] || '');
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setSelectedSuggestion(prev => Math.min(suggestions.length - 1, prev + 1));
      } else if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(history[history.length - 1 - newIndex] || '');
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setInput('');
      }
      return;
    }
  };

  if (!open) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50"
      data-testid="command-mode"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        style={{ top: '-100vh' }}
      />

      {/* Command Bar */}
      <div className="relative bg-[#0a0e27] border-t border-white/20">
        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="border-b border-white/10">
            {suggestions.map(([cmd, info], i) => (
              <button
                key={cmd}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2 text-sm",
                  i === selectedSuggestion ? "bg-blue-500/20" : "hover:bg-white/5"
                )}
                onClick={() => executeCommand(cmd)}
              >
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-blue-400">:{cmd}</span>
                  <span className="text-gray-500">{info.description}</span>
                </div>
                {i === selectedSuggestion && (
                  <kbd className="px-1.5 py-0.5 text-[10px] bg-white/10 rounded">Tab</kbd>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="flex items-center px-4 py-3">
          <Terminal className="w-4 h-4 text-green-400 mr-2" />
          <span className="text-green-400 mr-1">:</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none text-sm font-mono"
            placeholder="Type a command..."
            autoComplete="off"
            spellCheck="false"
          />
          
          {/* Last result indicator */}
          {lastResult && (
            <div className={cn(
              "flex items-center space-x-2 text-xs",
              lastResult.success ? "text-green-400" : "text-red-400"
            )}>
              {lastResult.success ? (
                <CheckCircle className="w-3 h-3" />
              ) : (
                <XCircle className="w-3 h-3" />
              )}
              <span>{lastResult.message}</span>
            </div>
          )}

          <kbd className="ml-4 px-2 py-1 text-xs bg-white/10 rounded text-gray-500">
            ESC
          </kbd>
        </div>

        {/* Help hint */}
        <div className="px-4 pb-2 text-[10px] text-gray-600">
          Type <code className="px-1 bg-white/10 rounded">help</code> for commands, 
          <code className="px-1 bg-white/10 rounded mx-1">↑↓</code> for history,
          <code className="px-1 bg-white/10 rounded">Tab</code> to complete
        </div>
      </div>
    </div>
  );
};

export default CommandMode;
